import React, {memo} from 'react'
import {keys, omit, orderBy} from 'lodash'
import {Button, Form, Header, List, Message, Modal, Radio, Table} from 'semantic-ui-react'
import {withErrorHandling, withHelmet, withMeData, withPropsPresent, withServiceData, withServicesData} from '../../hoc'
import {getPublishedOrFirstVersion, goBack} from '../../util'
import {upsertService} from '../../backend'
import {useSafeState} from '../../hook'

const isServicePresent = (services, name) => {
  return services.some((s) => s.name.toLowerCase() === name.toLowerCase())
}

const _ServiceUpsert = (props) => {
  const {
    services = [],
    service,
    mutateMe,
    mutateService,
    mutateServices,
    upsertService,
    history,
    navigate,
    basePath = '/c/services'
  } = props

  const [state, setState] = useSafeState({
    ...{
      name: '',
      internalURL: '',
      security: {enabled: false},
      submitted: false,
      displayNameWarning: false,
      logRedactions: [],
      newLogRedactionPath: '',
      newLogRedactionMatch: '',
      errors: {}
    },
    ...(service || {}),
    service: (service || {})
  })

  const validate = ({name, service, errors = {}}) => {
    if (!name) {
      errors['service.name.required'] = 'Name required'
    } else {
      delete errors['service.name.required']
      if (name.length > 50) {
        errors['service.name.exceeds.max.length'] = 'Name cannot exceed 50 characters'
      } else {
        delete errors['service.name.exceeds.max.length']
        if ((service.name !== name && isServicePresent(services, name))) {
          errors['service.taken'] = 'Name already present. Change name and try again'
        } else {
          delete errors['service.taken']
        }
      }
    }
    return errors
  }

  const onInputChange = (e) => {
    let updatedState = {...state, ...{[e.target.name]: e.target.value}}
    let errors = {}
    if (state.submitted) {
      errors = validate(updatedState)
      updatedState = {...updatedState, ...{errors}}
    }
    setState(updatedState)
  }

  const onNameFocus = () => {
    setState({...state, displayNameWarning: !!(service || {}).id})
  }

  const onClose = () => {
    goBack(history, navigate, basePath)
  }

  const onAddLogRedaction = (e) => {
    e.preventDefault()
    if (!state.newLogRedactionPath) {
      setState({
        ...state,
        errors: {'service.log.redaction.path.required': 'Log redaction path required'}
      })
      return
    }
    if (!['req.headers', 'req.body', 'res.headers', 'res.body'].some(e => state.newLogRedactionPath.startsWith(e) || state.newLogRedactionPath === e)) {
      setState({
        ...state,
        errors: {'service.log.redaction.path.invalid': 'Log redaction path must start with req.headers, req.body, res.headers, or res.body'}
      })
      return
    }

    if (['req.headers', 'res.headers'].some(e => state.newLogRedactionPath.startsWith(e)) && state.newLogRedactionPath.split('.').length !== 3) {
      setState({
        ...state,
        errors: {
          'service.log.redaction.path.invalid': (
            <>
              <p>Log redaction path must match exactly one header value when specifying req.headers or res.headers. </p>
              <p> (e.g. req.headers.Authorization or res.headers["Authorization"]</p>
            </>
          )
        }
      })
      return
    }
    setState(
      {
        ...state,
        errors: {},
        logRedactions: [...state.logRedactions, {path: state.newLogRedactionPath, match: state.newLogRedactionMatch}],
        newLogRedactionPath: '',
        newLogRedactionMatch: ''
      }
    )
  }

  const onRemoveLogRedaction = (e, idx) => {
    e.preventDefault()
    const {logRedactions = []} = state
    const reducedLogRedactions = [...logRedactions]
    reducedLogRedactions.splice(idx, 1)
    setState({...state, logRedactions: reducedLogRedactions})
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    if (keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    setState({...state, submitting: true})
    let service = await upsertService(state)
    service = omit({...service, ...state}, ['service'])
    mutateServices()
    mutateService()
    mutateMe()
    const version = getPublishedOrFirstVersion(service)
    navigate(`${basePath}/${service.id}/versions/${version.id}/environments`)
  }

  const onToggleSecurity = async (enabled) => {
    let {security} = state
    security = {...security, enabled: !enabled}
    setState({...state, security})
  }

  const logRedactions = orderBy(state.logRedactions, ['path'], ['asc'])

  const {name, security, internalURL, errors, submitting} = state
  const editing = !!state.service.id
  const hasServiceTakenError = keys(errors).some((k) => k.indexOf('service.taken') > -1)
  const hasNameError = keys(errors).some((k) => k.indexOf('service.name') > -1)
  const hasLogRedactionError = keys(errors).some((k) => k.indexOf('service.log.redaction.path') > -1)
  const hasErrors = hasServiceTakenError || hasNameError || hasLogRedactionError

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose}
           style={{position: '!important relative'}} data-testid='service-upsert-modal'>
      <Header content={`${editing ? 'Edit Service' : 'Add Service'}`}/>
      <Modal.Content>
        <Form error={hasErrors}>
          <Form.Field error={hasNameError || hasServiceTakenError} required>
            <label>Name</label>
            <Form.Input placeholder='Enter service name'
                        name='name'
                        value={name}
                        data-testid='service-name-textbox'
                        onFocus={onNameFocus}
                        onChange={onInputChange}/>
          </Form.Field>
          <Message hidden={!state.displayNameWarning || !service} size='mini'
                   content="Changing 'name' results in changes to Mock/Docs URL's"/>
          <Form.Field>
            <label>Internal URL (optional):</label>
            <Form.Input placeholder='Enter internal url'
                        name='internalURL'
                        data-testid='service-internal-url-textbox'
                        value={internalURL}
                        onChange={onInputChange}/>
          </Form.Field>
          <Form.Field>
            <label>Log Redactions</label>
            {
              !!logRedactions.length && (
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Path</Table.HeaderCell>
                      <Table.HeaderCell>Match</Table.HeaderCell>
                      <Table.HeaderCell></Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {
                      logRedactions.length > 0 && logRedactions.map((lr, idx) => (
                        <Table.Row key={`lr_${idx}`}>
                          <Table.Cell>{lr.path}</Table.Cell>
                          <Table.Cell>{lr.match || 'n/a'}</Table.Cell>
                          <Table.Cell><Button onClick={(e) => onRemoveLogRedaction(e, idx)} color='red'
                                              size='tiny'>Remove</Button> </Table.Cell>
                        </Table.Row>
                      ))
                    }
                  </Table.Body>
                </Table>
              )
            }
            {
              <Form.Group widths='equal'>
                <Form.Field error={hasLogRedactionError} required>
                  <Form.Input name='newLogRedactionPath' fluid placeholder='Path (e.g. res.body.username, req.headers.Authorization)'
                              value={state.newLogRedactionPath}
                              onChange={onInputChange}/>
                </Form.Field>
                <Form.Input name='newLogRedactionMatch' fluid placeholder='Match (optional)'
                            value={state.newLogRedactionMatch}
                            onChange={onInputChange}/>
              </Form.Group>
            }
            <Button onClick={onAddLogRedaction}>Add</Button>
          </Form.Field>
          <Form.Field>
            <label>API Key Required</label>
            <Radio data-testid='service-api-key-required-radio' toggle checked={security.enabled}
                   onChange={() => onToggleSecurity(security.enabled)}/>
          </Form.Field>
          {
            security.enabled && (
              <Message size='mini' data-testid='service-api-key-required-message'>
                Mock clients will be required to send the 'x-smocks-api-key' header. After saving, see environment(s)
                for valid key(s).
              </Message>
            )
          }
          <Message error data-testid='service-upsert-errors'>
            <Message.Content>
              <List bulleted>
                {
                  keys(errors).map((key) => (
                    <List.Item key={key}>{errors[key]}</List.Item>
                  ))
                }
              </List>
            </Message.Content>
          </Message>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='service-upsert-cancel-button'>Cancel</Button>
        <Button primary onClick={onSubmit}
                data-testid='service-upsert-submit-button'
                loading={submitting}>{`${editing ? 'Update' : 'Add'}`}</Button>
      </Modal.Actions>
    </Modal>
  )
}

let UpsertService = (props) => {
  const {handleError} = props
  props = {
    ...props,
    history: window.history,
    upsertService: handleError(upsertService)
  }
  return (<_ServiceUpsert {...props}/>)
}


UpsertService = withHelmet(UpsertService, (props) => {
  const {service} = props
  if (service && service.id) {
    return 'Edit Service'
  }
  return 'Add Service'
})
UpsertService = withPropsPresent(UpsertService, [(props) => !props.serviceId || !!props.service])
UpsertService = withServiceData(UpsertService, 'Loading service...')
UpsertService = withServicesData(UpsertService, 'Loading services...')
UpsertService = withMeData(UpsertService, 'Loading profile...')
UpsertService = withErrorHandling(UpsertService)
UpsertService = memo(UpsertService)

export {_ServiceUpsert, UpsertService, isServicePresent}

export default UpsertService